import React, { useState, useMemo, useCallback } from 'react';
import { calcLoan, buildAmortization, fmt, fmtShort, type LoanInputs } from '@/lib/loanCalc';

// ── Constants ──────────────────────────────────────────────────────────────────

const PRESETS = [
{ label: 'Polaris Sportsman 570', sub: 'Utility', msrp: 8299 },
{ label: 'Can-Am Outlander 700', sub: 'Sport/Utility', msrp: 7499 },
{ label: 'Yamaha Grizzly 700 EPS', sub: 'Classic Trail', msrp: 11399 },
{ label: 'Honda Pioneer 1000', sub: 'Workhorse', msrp: 17499 },
{ label: 'Polaris RZR XP 1000', sub: 'Premium SxS', msrp: 20999 },
{ label: 'Can-Am Maverick R', sub: 'Ultra High-Performance', msrp: 35499 }];


const FICO_TIERS = [
{ label: 'Excellent', range: '740+', apr: 6.49, color: 'text-emerald-400' },
{ label: 'Good', range: '670–739', apr: 8.99, color: 'text-blue-400' },
{ label: 'Fair', range: '580–669', apr: 13.49, color: 'text-yellow-400' },
{ label: 'Bad/Subprime', range: '<580', apr: 18.99, color: 'text-red-400' }];


const STATE_TAX = [
{ label: 'Texas', rate: 6.25 },
{ label: 'California', rate: 7.25 },
{ label: 'Florida', rate: 6.0 }];


const TERMS = [24, 36, 48, 60, 72, 84];

const FAQS = [
{
  q: 'How long can you finance an ATV?',
  a: 'Most powersports lenders offer loan terms between 24 and 72 months, with some extending to 84 months. Shorter terms (24–36 months) save the most money on compound interest — a 36-month loan on a $12,000 ATV at 8.99% APR saves over $1,400 compared to stretching it to 72 months. However, 72–84 month loans can cut your monthly SxS amortization payment significantly, which may suit buyers on tighter budgets. The key tradeoff: longer term = lower monthly bill but higher total cost of ownership.'
},
{
  q: 'Can I get an ATV loan with bad credit?',
  a: "Yes — powersports subprime lenders specialize in approving buyers with credit scores below 580. Expect APR rates of 18%+ and stricter terms. The single most effective strategy is a larger down payment: putting down at least 15% of the vehicle's MSRP dramatically improves your approval odds and reduces the lender's risk exposure. Our UTV loan calculator with down payment feature shows exactly how an upfront payment cuts your monthly bill and total interest. Using a co-signer with strong credit is another proven path to better terms."
},
{
  q: 'How do dealer freight and setup fees impact my loan size?',
  a: "Dealer destination charges, documentation fees, and setup/prep surcharges are rarely advertised in MSRP sticker pricing but routinely add $500–$1,200 to your out-the-door cost. Since most buyers roll these into their financed loan, that extra $750 in fees doesn't just cost $750 — it accrues interest for the entire loan term. On a 60-month loan at 13.49% APR, a $750 dealer fee adds roughly $270 in extra interest, bringing the true cost to over $1,000. Always ask dealers for an itemized out-the-door quote before signing."
}];


// ── Sub-components ─────────────────────────────────────────────────────────────

function SliderInput({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format,
  hint









}: {label: string;value: number;min: number;max: number;step: number;onChange: (v: number) => void;format: (v: number) => string;hint?: string;}) {
  const pct = (value - min) / (max - min) * 100;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-300">{label}</label>
        <span className="font-mono-num text-sm font-semibold text-orange-400">{format(value)}</span>
      </div>
      <div className="relative flex items-center">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="slider-orange w-full"
          style={{ '--slider-pct': `${pct}%` } as React.CSSProperties} />

      </div>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>);

}

function NumberInput({
  label,
  value,
  onChange,
  prefix,
  suffix,
  min,
  max,
  hint









}: {label: string;value: number;onChange: (v: number) => void;prefix?: string;suffix?: string;min?: number;max?: number;hint?: string;}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-slate-300">{label}</label>
      <div className="flex items-center bg-slate-800 border border-slate-700 rounded-xl overflow-hidden focus-within:border-orange-500 transition-colors">
        {prefix && <span className="pl-3 pr-1 text-slate-400 font-mono-num text-sm">{prefix}</span>}
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 bg-transparent px-3 py-3 text-sm font-mono-num text-white outline-none min-w-0" />

        {suffix && <span className="pr-3 pl-1 text-slate-400 font-mono-num text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>);

}

function ChipButton({
  active,
  onClick,
  children




}: {active: boolean;onClick: () => void;children: React.ReactNode;}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-150 ${
      active ?
      'bg-orange-500 text-white shadow-lg shadow-orange-500/20 scale-105' :
      'bg-slate-700/60 text-slate-300 hover:bg-slate-600 hover:text-white'}`
      }>

      {children}
    </button>);

}

function FaqItem({ q, a }: {q: string;a: string;}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-700/60 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-800/50 transition-colors">

        <h3 className="font-semibold text-white text-sm pr-4">{q}</h3>
        <span
          className={`text-orange-400 text-lg transition-transform duration-300 flex-shrink-0 ${
          open ? 'rotate-180' : 'rotate-0'}`
          }>

          ▾
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
        open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`
        }>

        <p className="px-5 pb-5 text-sm text-slate-400 leading-relaxed">{a}</p>
      </div>
    </div>);

}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function Home() {
  const [msrp, setMsrp] = useState(8299);
  const [downPayment, setDownPayment] = useState(1000);
  const [tradeIn, setTradeIn] = useState(0);
  const [taxRate, setTaxRate] = useState(6.25);
  const [dealerFees, setDealerFees] = useState(750);
  const [apr, setApr] = useState(6.49);
  const [termMonths, setTermMonths] = useState(60);
  const [extraMonthly, setExtraMonthly] = useState(0);
  const [amortOpen, setAmortOpen] = useState(false);
  const [activePreset, setActivePreset] = useState(0);
  const [activeFico, setActiveFico] = useState(0);

  const inputs: LoanInputs = useMemo(
    () => ({ msrp, downPayment, tradeIn, taxRate, dealerFees, apr, termMonths, extraMonthly }),
    [msrp, downPayment, tradeIn, taxRate, dealerFees, apr, termMonths, extraMonthly]
  );

  const results = useMemo(() => calcLoan(inputs), [inputs]);
  const amortRows = useMemo(
    () => buildAmortization(results.principal, apr, termMonths, results.monthlyPayment),
    [results.principal, apr, termMonths, results.monthlyPayment]
  );

  const selectPreset = useCallback((i: number) => {
    setActivePreset(i);
    setMsrp(PRESETS[i].msrp);
  }, []);

  const selectFico = useCallback((i: number) => {
    setActiveFico(i);
    setApr(FICO_TIERS[i].apr);
  }, []);

  const principalPct = results.totalPaid > 0 ?
  results.principal / results.totalPaid * 100 :
  0;
  const interestPct = 100 - principalPct;

  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-100" style={{ fontFamily: "'Inter', 'Space Grotesk', sans-serif" }}>
      {/* ── Header ── */}
      <header className="border-b border-slate-700/50 bg-[#0F172A]/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-white" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4l2.5 2.5" strokeLinecap="round" />
              </svg>
            </div>
            <span className="font-bold text-white text-sm tracking-tight">ATVLoanCalculator<span className="text-orange-400">.com</span></span>
          </div>
          <a
            href="#cta"
            className="hidden sm:inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-400 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-all duration-150 hover:scale-[1.02]">


          </a>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
        <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
          <span>⚡</span> Free · No Login Required · Instant Results
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          ATV Loan Calculator
          <span className="block text-orange-400 mt-1">Fast Estimate &amp; Amortization Payment Tool</span>
        </h1>
        <p className="text-slate-400 max-w-2xl text-base leading-relaxed">
          The most accurate <strong className="text-slate-300">4 wheeler payment estimator</strong> for Polaris, Can-Am, Yamaha &amp; Honda powersports.
          Factor in sales tax, dealer surcharges, trade-in equity, and FICO-based APR — your complete{' '}
          <strong className="text-slate-300">UTV loan calculator with down payment</strong> tool.
        </p>
      </section>

      {/* ── Calculator Layout ── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── LEFT PANEL: Inputs ── */}
          <div className="w-full lg:w-[480px] xl:w-[520px] flex-shrink-0 space-y-5">

            {/* Vehicle Presets */}
            <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 space-y-4">
              <h2 className="text-sm font-bold text-slate-200 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1.5 h-4 bg-orange-500 rounded-full" />
                Select Vehicle Preset
              </h2>
              <div className="grid grid-cols-2 gap-2">
                {PRESETS.map((p, i) =>
                <button
                  key={p.label}
                  onClick={() => selectPreset(i)}
                  className={`text-left p-3 rounded-xl border transition-all duration-150 ${
                  activePreset === i ?
                  'border-orange-500 bg-orange-500/10 shadow-lg shadow-orange-500/10' :
                  'border-slate-700 bg-slate-800/40 hover:border-slate-500'}`
                  }>

                    <div className={`text-xs font-bold leading-tight ${activePreset === i ? 'text-orange-400' : 'text-slate-200'}`}>
                      {p.label}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{p.sub}</div>
                    <div className={`font-mono-num text-sm font-bold mt-1 ${activePreset === i ? 'text-white' : 'text-slate-300'}`}>
                      {fmtShort(p.msrp)}
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Vehicle & Loan Parameters */}
            <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 space-y-5">
              <h2 className="text-sm font-bold text-slate-200 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1.5 h-4 bg-orange-500 rounded-full" />
                Loan Parameters
              </h2>

              <SliderInput
                label="Vehicle MSRP / Sticker Price"
                value={msrp}
                min={2000}
                max={100000}
                step={100}
                onChange={setMsrp}
                format={fmtShort}
                hint="Adjust or select a preset above" />


              <div className="grid grid-cols-2 gap-4">
                <NumberInput
                  label="Down Payment"
                  value={downPayment}
                  onChange={setDownPayment}
                  prefix="$"
                  min={0}
                  hint="0 = no money down" />

                <NumberInput
                  label="Trade-In Equity"
                  value={tradeIn}
                  onChange={setTradeIn}
                  prefix="$"
                  min={0}
                  hint="Your current ATV value" />

              </div>

              {/* Tax Rate */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300">Sales Tax Rate</label>
                  <span className="font-mono-num text-sm font-semibold text-orange-400">{taxRate.toFixed(2)}%</span>
                </div>
                <div className="flex gap-2 flex-wrap mb-2">
                  {STATE_TAX.map((s) =>
                  <button
                    key={s.label}
                    onClick={() => setTaxRate(s.rate)}
                    className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-all ${
                    taxRate === s.rate ?
                    'bg-orange-500/20 border border-orange-500/60 text-orange-400' :
                    'bg-slate-700/50 border border-slate-600 text-slate-400 hover:text-slate-200'}`
                    }>

                      {s.label} {s.rate}%
                    </button>
                  )}
                </div>
                <input
                  type="range"
                  min={0}
                  max={12}
                  step={0.25}
                  value={taxRate}
                  onChange={(e) => setTaxRate(Number(e.target.value))}
                  className="slider-orange w-full"
                  style={{ '--slider-pct': `${taxRate / 12 * 100}%` } as React.CSSProperties} />

              </div>

              <NumberInput
                label="Dealer Freight, Doc & Prep Fees"
                value={dealerFees}
                onChange={setDealerFees}
                prefix="$"
                min={0}
                hint="Typically $500–$1,200. These fees accrue interest!" />

            </div>

            {/* FICO / APR Tier */}
            <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 space-y-4">
              <h2 className="text-sm font-bold text-slate-200 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1.5 h-4 bg-orange-500 rounded-full" />
                Credit Score / APR
              </h2>
              <div className="grid grid-cols-2 gap-2">
                {FICO_TIERS.map((f, i) =>
                <button
                  key={f.label}
                  onClick={() => selectFico(i)}
                  className={`text-left p-3 rounded-xl border transition-all duration-150 ${
                  activeFico === i ?
                  'border-orange-500 bg-orange-500/10' :
                  'border-slate-700 bg-slate-800/40 hover:border-slate-500'}`
                  }>

                    <div className={`text-xs font-bold ${f.color}`}>{f.label}</div>
                    <div className="text-xs text-slate-500">{f.range}</div>
                    <div className="font-mono-num text-sm font-bold text-white mt-1">{f.apr}% APR</div>
                  </button>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-300">Custom APR</label>
                  <span className="font-mono-num text-sm font-semibold text-orange-400">{apr.toFixed(2)}%</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={29.99}
                  step={0.01}
                  value={apr}
                  onChange={(e) => setApr(Number(e.target.value))}
                  className="slider-orange w-full"
                  style={{ '--slider-pct': `${(apr - 1) / (29.99 - 1) * 100}%` } as React.CSSProperties} />

              </div>
            </div>

            {/* Term & Extra Payment */}
            <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 space-y-4">
              <h2 className="text-sm font-bold text-slate-200 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1.5 h-4 bg-orange-500 rounded-full" />
                Loan Term &amp; Accelerator
              </h2>
              <div>
                <label className="text-sm font-medium text-slate-300 block mb-2">Repayment Term</label>
                <div className="flex gap-2 flex-wrap">
                  {TERMS.map((t) =>
                  <ChipButton key={t} active={termMonths === t} onClick={() => setTermMonths(t)}>
                      {t} mo
                    </ChipButton>
                  )}
                </div>
              </div>
              <NumberInput
                label="Extra Monthly Principal Payment"
                value={extraMonthly}
                onChange={setExtraMonthly}
                prefix="$"
                min={0}
                hint="Pay ahead each month to save interest and retire the loan early" />

            </div>
          </div>

          {/* ── RIGHT PANEL: Results ── */}
          <div className="flex-1 space-y-5">

            {/* Hero Result Card */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 shadow-2xl shadow-orange-500/20">
              <p className="text-orange-100 text-sm font-medium mb-1">Estimated Monthly Payment</p>
              <div className="flex items-end gap-3">
                <span className="font-mono-num text-5xl sm:text-6xl font-bold text-white leading-none">
                  {fmt(results.monthlyPayment)}
                </span>
                <span className="text-orange-200 text-sm pb-2">/month</span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="bg-white/10 rounded-xl p-3">
                  <p className="text-orange-200 text-xs font-medium">Loan Principal</p>
                  <p className="font-mono-num text-white font-bold text-sm mt-0.5">{fmtShort(results.principal)}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-3">
                  <p className="text-orange-200 text-xs font-medium">Total Interest</p>
                  <p className="font-mono-num text-white font-bold text-sm mt-0.5">{fmtShort(results.totalInterest)}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-3">
                  <p className="text-orange-200 text-xs font-medium">Total Cost</p>
                  <p className="font-mono-num text-white font-bold text-sm mt-0.5">{fmtShort(results.totalPaid + downPayment + tradeIn)}</p>
                </div>
              </div>
            </div>

            {/* Cost Breakdown Bar */}
            <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 space-y-3">
              <h2 className="text-sm font-bold text-slate-200 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1.5 h-4 bg-orange-500 rounded-full" />
                Principal vs. Interest Breakdown
              </h2>
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-slate-400 mb-1">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
                    Principal — {fmtShort(results.principal)} ({principalPct.toFixed(1)}%)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-orange-400 inline-block" />
                    Interest — {fmtShort(results.totalInterest)} ({interestPct.toFixed(1)}%)
                  </span>
                </div>
                <div className="w-full h-5 bg-slate-800 rounded-full overflow-hidden flex">
                  <div
                    className="bg-blue-500 h-full transition-all duration-500 ease-out"
                    style={{ width: `${principalPct}%` }} />

                  <div
                    className="bg-orange-500 h-full transition-all duration-500 ease-out"
                    style={{ width: `${interestPct}%` }} />

                </div>
                <p className="text-xs text-slate-500">
                  You'll pay <span className="text-orange-400 font-semibold">{fmtShort(results.totalInterest)}</span> in interest
                  over {termMonths} months — that's{' '}
                  <span className="text-orange-400 font-semibold">{interestPct.toFixed(1)}%</span> of your total loan cost.
                </p>
              </div>
            </div>

            {/* Payoff Accelerator */}
            {extraMonthly > 0 &&
            <div className="bg-emerald-900/30 border border-emerald-500/40 rounded-2xl p-5 space-y-3">
                <h2 className="text-sm font-bold text-emerald-300 uppercase tracking-widest flex items-center gap-2">
                  <span>⚡</span> Payoff Acceleration Results
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-emerald-500/10 rounded-xl p-4">
                    <p className="text-emerald-300 text-xs font-medium">Months Saved</p>
                    <p className="font-mono-num text-3xl font-bold text-white mt-1">
                      {results.monthsSaved}
                    </p>
                    <p className="text-emerald-400 text-xs mt-1">
                      {results.monthsSaved > 0 ?
                    `Shave ${results.monthsSaved} months off your loan!` :
                    'Pay extra to save time'}
                    </p>
                  </div>
                  <div className="bg-emerald-500/10 rounded-xl p-4">
                    <p className="text-emerald-300 text-xs font-medium">Interest Saved</p>
                    <p className="font-mono-num text-3xl font-bold text-white mt-1">
                      {fmtShort(results.interestSaved)}
                    </p>
                    <p className="text-emerald-400 text-xs mt-1">Total compound savings</p>
                  </div>
                </div>
                <div className="bg-emerald-500/10 rounded-xl p-3 flex items-center gap-3">
                  <span className="text-2xl">🏁</span>
                  <p className="text-sm text-emerald-200">
                    With <span className="font-mono-num font-bold text-white">{fmtShort(extraMonthly)}/mo</span> extra, you'll pay off
                    your ATV in <span className="font-bold text-white">{results.acceleratedMonths} months</span> instead of {termMonths}.
                  </p>
                </div>
              </div>
            }

            {/* Loan Summary Table */}
            <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 space-y-3">
              <h2 className="text-sm font-bold text-slate-200 uppercase tracking-widest flex items-center gap-2">
                <span className="w-1.5 h-4 bg-orange-500 rounded-full" />
                Full Loan Summary
              </h2>
              <div className="space-y-2">
                {[
                { label: 'Vehicle MSRP', value: fmtShort(msrp) },
                { label: 'Down Payment', value: `−${fmtShort(downPayment)}`, negative: true },
                { label: 'Trade-In Credit', value: `−${fmtShort(tradeIn)}`, negative: true },
                { label: `Sales Tax (${taxRate.toFixed(2)}%)`, value: `+${fmtShort(msrp * taxRate / 100)}`, positive: true },
                { label: 'Dealer Fees', value: `+${fmtShort(dealerFees)}`, positive: true },
                { label: 'Financed Principal', value: fmtShort(results.principal), bold: true },
                { label: 'APR Rate', value: `${apr.toFixed(2)}%` },
                { label: 'Loan Term', value: `${termMonths} months` },
                { label: 'Monthly Payment', value: fmt(results.monthlyPayment), bold: true, highlight: true },
                { label: 'Total Interest Paid', value: fmtShort(results.totalInterest), orange: true },
                { label: 'Total Loan Cost', value: fmtShort(results.totalPaid), bold: true }].
                map(({ label, value, bold, highlight, negative, positive, orange }) =>
                <div key={label} className={`flex justify-between items-center py-1.5 ${bold ? 'border-t border-slate-700/50 mt-1 pt-2.5' : ''}`}>
                    <span className={`text-sm ${bold ? 'font-semibold text-slate-200' : 'text-slate-400'}`}>{label}</span>
                    <span
                    className={`font-mono-num text-sm font-semibold ${
                    highlight ?
                    'text-orange-400 text-base' :
                    orange ?
                    'text-orange-400' :
                    negative ?
                    'text-emerald-400' :
                    positive ?
                    'text-red-400' :
                    bold ?
                    'text-white' :
                    'text-slate-300'}`
                    }>

                      {value}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Amortization Schedule */}
            <div className="bg-[#1E293B] rounded-2xl border border-slate-700/50 overflow-hidden">
              <button
                onClick={() => setAmortOpen((o) => !o)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-800/30 transition-colors">

                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-orange-500 rounded-full" />
                  <h2 className="text-sm font-bold text-slate-200 uppercase tracking-widest">
                    SxS Amortization Schedule ({termMonths} months)
                  </h2>
                </div>
                <span className={`text-orange-400 text-xl transition-transform duration-300 ${amortOpen ? 'rotate-180' : ''}`}>
                  ▾
                </span>
              </button>
              <div className={`overflow-hidden transition-all duration-400 ${amortOpen ? 'max-h-[600px]' : 'max-h-0'}`}>
                <div className="overflow-auto scrollbar-thin max-h-[560px]">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[#1E293B] border-y border-slate-700/50">
                      <tr>
                        {['Month', 'Payment', 'Principal', 'Interest', 'Balance'].map((h) =>
                        <th key={h} className="px-4 py-2.5 text-left font-semibold text-slate-400 uppercase tracking-wider">
                            {h}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {amortRows.map((row, idx) =>
                      <tr
                        key={row.month}
                        className={`border-t border-slate-800/60 ${
                        idx % 2 === 0 ? 'bg-slate-800/10' : ''} hover:bg-orange-500/5 transition-colors`
                        }>

                          <td className="px-4 py-2 font-mono-num text-slate-400">{row.month}</td>
                          <td className="px-4 py-2 font-mono-num text-slate-200">{fmt(row.payment)}</td>
                          <td className="px-4 py-2 font-mono-num text-blue-400">{fmt(row.principalPaid)}</td>
                          <td className="px-4 py-2 font-mono-num text-orange-400">{fmt(row.interestPaid)}</td>
                          <td className="px-4 py-2 font-mono-num text-slate-300">{fmt(row.balance)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── SEO Content Section ── */}
        <section className="mt-16 max-w-4xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-4 mb-12">
            {[
            {
              icon: '📐',
              title: 'Quad Finance Tracker',
              body: 'Track every dollar of your quad or ATV financing — from MSRP to final payoff. Our engine handles sales tax, dealer surcharges, and trade-in credits instantly.'
            },
            {
              icon: '🏍️',
              title: 'Can-Am Defender Loan Estimator',
              body: 'Whether you\'re financing a Can-Am Defender, Maverick, or Outlander, our SxS amortization schedule breaks down every monthly payment period-by-period.'
            },
            {
              icon: '📊',
              title: 'SxS Amortization Schedule',
              body: 'View the complete SxS amortization schedule for your side-by-side. See exactly how much of each payment goes to principal vs. interest over time.'
            }].
            map(({ icon, title, body }) =>
            <div key={title} className="bg-[#1E293B] rounded-2xl border border-slate-700/50 p-5 space-y-2">
                <div className="text-2xl">{icon}</div>
                <h3 className="font-bold text-white text-sm">{title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
              </div>
            )}
          </div>

          {/* FAQ Block */}
          <div className="space-y-3 mb-12">
            <h2 className="text-2xl font-extrabold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Frequently Asked Questions
            </h2>
            {FAQS.map((f) =>
            <FaqItem key={f.q} q={f.q} a={f.a} />
            )}
          </div>
        </section>

        {/* ── CTA Section ── */}
        






























      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-slate-700/50 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-orange-500 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" className="w-3.5 h-3.5 text-white" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4l2.5 2.5" strokeLinecap="round" />
              </svg>
            </div>
            <span className="text-sm font-bold text-slate-300">ATVLoanCalculator.com</span>
          </div>
          <p className="text-xs text-slate-600 text-center">
            For informational purposes only. Rates and payments are estimates only. Consult a licensed lender for actual financing terms.
          </p>
          <p className="text-xs text-slate-600">© {new Date().getFullYear()} ATVLoanCalculator.com</p>
        </div>
      </footer>
    </div>);

}